import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Menu'
import { Portal } from 'reakit/Portal'

import { Box, BoxProps, BoxRounding } from '../box'
import { useZIndex } from '../dialog'
import { DropdownContext, useDropdownState } from './hooks'

interface DropdownBaseProps extends Omit<BoxProps, 'label'> {
  /**
   * Trigger button for the dropdown
   */
  label: JSX.Element

  /**
   * @private
   * Any additional props to the trigger button. Used by nested dropdown wrapper
   */
  triggerProps?: Reakit.MenuButtonHTMLProps

  /**
   * Rounding of all trigger button, dropdown wrapper and dropdown items
   */
  rounding?: keyof typeof BoxRounding
}

interface RootDropdownProps {
  /**
   * The returned object from `useDropdownState`. For nested dropdowns use the `isNested` prop
   */
  state: Reakit.MenuStateReturn
  isNested?: never
  /**
   * When set to true the dropdown will not render in Portal and will not use position absolute + transform.
   */
  isStatic?: boolean
}

interface NestedDropdownProps {
  state?: never
  /**
   * When set to true the dropdown will compute its own state. Only usable with
   * nested dropdowns
   */
  isNested: true

  isStatic?: never
}

type DropdownProps = DropdownBaseProps & (RootDropdownProps | NestedDropdownProps)

function DropdownComponent(
  {
    label,
    triggerProps,
    state,
    'aria-label': ariaLabel,
    children,
    isNested,
    rounding,
    isStatic,
    ...props
  }: React.PropsWithChildren<DropdownProps>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const zIndex = useZIndex()
  const context = React.useContext(DropdownContext)
  // Required due to the way conditional props work
  const dropdownState = state!
  if (!isNested && !dropdownState) {
    throw new Error('Dropdowns require either `state` or `isNested` prop defined!')
  }

  const ContainerElement = React.useRef(isNested || isStatic ? React.Fragment : Portal).current

  return (
    <>
      <Reakit.MenuButton ref={ref} {...dropdownState} {...triggerProps}>
        {(buttonProps) =>
          React.cloneElement(label, {
            ...buttonProps,
            ...label.props,
            rounding,
            active: label.props.active ?? dropdownState.visible,
          })
        }
      </Reakit.MenuButton>
      <ContainerElement>
        <Reakit.Menu {...dropdownState} aria-label={ariaLabel}>
          {({ onTransitionEnd, onAnimationEnd, ...innerProps }) => (
            <div
              {...innerProps}
              className={classnames(zIndex, 'finviz-popover outline-none')}
              style={isStatic ? undefined : innerProps.style}
            >
              <DropdownContext.Provider value={{ parent: dropdownState, root: context?.root ?? dropdownState }}>
                <Box
                  {...props}
                  rounding={rounding}
                  onTransitionEnd={onTransitionEnd}
                  onAnimationEnd={onAnimationEnd}
                  data-placement={dropdownState.placement}
                  className={classnames(props.className, 'flex items-stretch p-1', {
                    'flex-col  space-y-px': dropdownState.orientation === 'vertical',
                    'space-x-px': dropdownState.orientation === 'horizontal',
                  })}
                >
                  {React.Children.map(children, (child) => {
                    if (!React.isValidElement(child)) return child

                    if (child.type === Dropdown) {
                      return <NestedDropdownWrapper parentState={dropdownState}>{child}</NestedDropdownWrapper>
                    }

                    return React.cloneElement(child, {
                      ...child.props,
                      rounding,
                    })
                  })}
                </Box>
              </DropdownContext.Provider>
            </div>
          )}
        </Reakit.Menu>
      </ContainerElement>
    </>
  )
}

export const Dropdown = React.forwardRef(DropdownComponent)

interface NestedDropdownWrapperProps {
  /**
   * State of parrent dropdown to be passed to MenuItem for correct navigation
   * handling
   */
  parentState: Reakit.MenuStateReturn

  /**
   * Nested dropdown element
   */
  children: JSX.Element
}

/**
 * The `useDropdownState` requires parent context to be defined in order to work
 * with nested Dropdowns. So we need to create a wrapper to be able to get to the
 * parent context
 */
function NestedDropdownWrapper({ parentState, children }: NestedDropdownWrapperProps) {
  const state = useDropdownState()

  return (
    <Reakit.MenuItem {...parentState}>
      {(itemProps) => React.cloneElement(children, { ...children.props, triggerProps: itemProps, state })}
    </Reakit.MenuItem>
  )
}
